
'use client';

import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip as ShadcnChartTooltip,
  ChartTooltipContent,
  ChartLegend as ShadcnChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { useIsMobile } from '@/hooks/use-mobile';

interface ChartSpecBase {
  type: 'bar' | 'line' | 'area' | 'radar' | 'composed' | 'pie';
  data: Record<string, unknown>[];
}

interface BarLineChartSpec extends ChartSpecBase {
  type: 'bar' | 'line';
  xAxisKey: string;
  seriesKeys: string[];
}

interface AreaChartSpec extends ChartSpecBase {
  type: 'area';
  xAxisKey: string;
  seriesKeys: string[];
}

interface RadarChartSpec extends ChartSpecBase {
  type: 'radar';
  angleKey: string;
  seriesKeys: string[];
}

interface ComposedSeriesSpec {
  key: string;
  render?: 'bar' | 'line' | 'area';
}

interface ComposedChartSpec extends ChartSpecBase {
  type: 'composed';
  xAxisKey: string;
  series: ComposedSeriesSpec[];
}

interface PieChartDataItem {
  [key: string]: unknown;
  fill?: string;
}

interface PieChartSpec extends ChartSpecBase {
  type: 'pie';
  dataKey: string;
  nameKey: string;
  data: PieChartDataItem[];
}

export type MarkdownChartSpec =
  | BarLineChartSpec
  | AreaChartSpec
  | RadarChartSpec
  | ComposedChartSpec
  | PieChartSpec;

interface MarkdownRechartsChartProps {
  chartSpec: MarkdownChartSpec;
}

const defaultChartColors = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

function getValidChartColor(
  jsonColorString: string | undefined,
  defaultColorFromPalette: string
): string {
  if (
    jsonColorString &&
    jsonColorString.startsWith('hsl(var(--chart-') &&
    jsonColorString.endsWith('))')
  ) {
    const match = jsonColorString.match(/--chart-(\d+)/);
    if (match) {
      const chartIndex = parseInt(match[1], 10);
      if (chartIndex >= 1 && chartIndex <= defaultChartColors.length) {
        return jsonColorString;
      }
    }
  }
  return defaultColorFromPalette;
}


export default function MarkdownRechartsChart({ chartSpec }: MarkdownRechartsChartProps) {
  const { type, data } = chartSpec;
  const isMobile = useIsMobile();

  let rechartsChartConfig: ChartConfig = {};

  if (!data || !Array.isArray(data) || data.length === 0) {
    return <p className="text-muted-foreground my-4">Keine Daten für das Diagramm vorhanden.</p>;
  }

  if (chartSpec.type === 'pie') {
    const pieSpec = chartSpec as PieChartSpec;
    if (!Array.isArray(pieSpec.data)) {
      return <p className="text-destructive my-4">Diagramm: pie.data fehlt oder ist ungueltig.</p>;
    }
    rechartsChartConfig = pieSpec.data.reduce((acc, entry, index) => {
      const name = entry[pieSpec.nameKey] as string | undefined;
      if (name) {
        acc[name] = {
          label: name,
          color: getValidChartColor(entry.fill, defaultChartColors[index % defaultChartColors.length]),
        };
      }
      return acc;
    }, {} as ChartConfig);
  } else if (chartSpec.type === 'bar' || chartSpec.type === 'line' || chartSpec.type === 'area' || chartSpec.type === 'radar') {
    if (!Array.isArray(chartSpec.seriesKeys)) {
      return <p className="text-destructive my-4">Diagramm: seriesKeys fehlt oder ist ungueltig.</p>;
    }
     rechartsChartConfig = chartSpec.seriesKeys.reduce((acc, key, index) => {
      acc[key] = {
        label: key,
        color: defaultChartColors[index % defaultChartColors.length],
      };
      return acc;
    }, {} as ChartConfig);
  } else if (chartSpec.type === 'composed') {
    const composedSpec = chartSpec as ComposedChartSpec;
    if (!Array.isArray(composedSpec.series)) {
      return <p className="text-destructive my-4">Diagramm: series fehlt oder ist ungueltig.</p>;
    }
    rechartsChartConfig = composedSpec.series.reduce((acc, s, index) => {
      acc[s.key] = {
        label: s.key,
        color: defaultChartColors[index % defaultChartColors.length],
      };
      return acc;
    }, {} as ChartConfig);
  }

  // Configuration values based on screen size
  const pieOuterRadius = isMobile ? 70 : 95;
  const pieInnerRadius = isMobile ? 30 : 45;
  const pieMargin = isMobile 
    ? { top: 60, right: 15, bottom: 20, left: 15 } // Adjusted bottom due to paddingTop for legend
    : { top: 60, right: 20, bottom: 20, left: 20 }; // Adjusted bottom
  const pieLegendPaddingTop = isMobile ? '50px' : '50px';
  const pieCyPosition = isMobile ? '50%' : '50%'; 

  const barLineXAxisHeight = isMobile ? 110 : 110; // Unified
  const barLineXAxisTickDy = isMobile ? 50 : 50;   // Unified
  const barLineMarginBottom = isMobile ? 130 : 130; // Unified
  const barLineChartMargin = {
    top: 20,
    right: 20, 
    left: 5,  
    bottom: barLineMarginBottom,
  };

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart
            accessibilityLayer
            data={data}
            margin={barLineChartMargin}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey={(chartSpec as BarLineChartSpec).xAxisKey}
              tickLine={false}
              axisLine={false}
              stroke="hsl(var(--muted-foreground))"
              angle={-35}
              textAnchor="end"
              minTickGap={1} 
              interval={0} 
              height={barLineXAxisHeight} 
              tick={{ fontSize: '10px', dy: barLineXAxisTickDy }}
            />
            <YAxis 
              tickLine={false} 
              axisLine={false} 
              stroke="hsl(var(--muted-foreground))" 
              tick={{ fontSize: '10px' }}
              width={40}
            />
            <ShadcnChartTooltip
                cursor={true}
                content={<ChartTooltipContent indicator="dot" hideLabel />}
            />
            {(chartSpec as BarLineChartSpec).seriesKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={defaultChartColors[index % defaultChartColors.length]}
                radius={[5, 5, 0, 0]}
              />
            ))}
            <ShadcnChartLegend content={<ChartLegendContent className="flex-wrap justify-center" />} />
          </BarChart>
        );
      case 'line':
        return (
           <LineChart
            accessibilityLayer
            data={data}
            margin={barLineChartMargin}
           >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey={(chartSpec as BarLineChartSpec).xAxisKey}
              tickLine={false}
              axisLine={false}
              stroke="hsl(var(--muted-foreground))"
              angle={-35}
              textAnchor="end"
              minTickGap={1}
              interval={0}
              height={barLineXAxisHeight}
              tick={{ fontSize: '10px', dy: barLineXAxisTickDy }}
            />
            <YAxis 
              tickLine={false} 
              axisLine={false} 
              stroke="hsl(var(--muted-foreground))" 
              tick={{ fontSize: '10px' }}
              width={40}
            />
            <ShadcnChartTooltip
                cursor={true}
                content={<ChartTooltipContent indicator="dot" hideLabel />}
            />
            {(chartSpec as BarLineChartSpec).seriesKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={defaultChartColors[index % defaultChartColors.length]}
                strokeWidth={2.5}
                dot={{ r: 4, fill: defaultChartColors[index % defaultChartColors.length], strokeWidth: 0 }}
                activeDot={{ r: 6, strokeWidth: 0, style: { boxShadow: '0 0 5px 2px ' + defaultChartColors[index % defaultChartColors.length] } }}
              />
            ))}
            <ShadcnChartLegend content={<ChartLegendContent className="flex-wrap justify-center" />} />
          </LineChart>
        );
      case 'pie':
        const pieSpec = chartSpec as PieChartSpec;
        return (
          <PieChart
            accessibilityLayer
            margin={pieMargin} 
          >
            <ShadcnChartTooltip
                cursor={true}
                content={<ChartTooltipContent indicator="dot" hideLabel />}
            />
            <Pie
              data={pieSpec.data} 
              dataKey={pieSpec.dataKey}
              nameKey={pieSpec.nameKey}
              cx="50%"
              cy={pieCyPosition} 
              outerRadius={pieOuterRadius} 
              innerRadius={pieInnerRadius} 
              labelLine={false}
            >
              {pieSpec.data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getValidChartColor(entry.fill, defaultChartColors[index % defaultChartColors.length])}
                />
              ))}
            </Pie>
            <ShadcnChartLegend
              content={<ChartLegendContent className="flex-wrap justify-center text-xs" />} 
              verticalAlign="bottom"
              wrapperStyle={{
                paddingTop: pieLegendPaddingTop, 
                width: '100%', 
              }}
            />
          </PieChart>
        );
      case 'area': {
        const areaSpec = chartSpec as AreaChartSpec;
        return (
          <AreaChart accessibilityLayer data={data} margin={barLineChartMargin}>
            <defs>
              {areaSpec.seriesKeys.map((key, index) => (
                <linearGradient key={key} id={`area-grad-${index}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={defaultChartColors[index % defaultChartColors.length]} stopOpacity={0.5} />
                  <stop offset="95%" stopColor={defaultChartColors[index % defaultChartColors.length]} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey={areaSpec.xAxisKey}
              tickLine={false}
              axisLine={false}
              stroke="hsl(var(--muted-foreground))"
              angle={-35}
              textAnchor="end"
              minTickGap={1}
              interval={0}
              height={barLineXAxisHeight}
              tick={{ fontSize: '10px', dy: barLineXAxisTickDy }}
            />
            <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: '10px' }} width={40} />
            <ShadcnChartTooltip cursor={true} content={<ChartTooltipContent indicator="dot" hideLabel />} />
            {areaSpec.seriesKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={defaultChartColors[index % defaultChartColors.length]}
                fill={`url(#area-grad-${index})`}
                strokeWidth={2.5}
              />
            ))}
            <ShadcnChartLegend content={<ChartLegendContent className="flex-wrap justify-center" />} />
          </AreaChart>
        );
      }
      case 'radar': {
        const radarSpec = chartSpec as RadarChartSpec;
        return (
          <RadarChart accessibilityLayer data={data} outerRadius={isMobile ? 70 : 95}>
            <PolarGrid />
            <PolarAngleAxis dataKey={radarSpec.angleKey} tick={{ fontSize: '11px', fill: 'hsl(var(--muted-foreground))' }} />
            <PolarRadiusAxis tick={{ fontSize: '10px', fill: 'hsl(var(--muted-foreground))' }} axisLine={false} />
            <ShadcnChartTooltip cursor={true} content={<ChartTooltipContent indicator="dot" hideLabel />} />
            {radarSpec.seriesKeys.map((key, index) => (
              <Radar
                key={key}
                name={key}
                dataKey={key}
                stroke={defaultChartColors[index % defaultChartColors.length]}
                fill={defaultChartColors[index % defaultChartColors.length]}
                fillOpacity={0.3}
                strokeWidth={2}
              />
            ))}
            <ShadcnChartLegend content={<ChartLegendContent className="flex-wrap justify-center" />} />
          </RadarChart>
        );
      }
      case 'composed': {
        const composedSpec = chartSpec as ComposedChartSpec;
        return (
          <ComposedChart accessibilityLayer data={data} margin={barLineChartMargin}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey={composedSpec.xAxisKey}
              tickLine={false}
              axisLine={false}
              stroke="hsl(var(--muted-foreground))"
              angle={-35}
              textAnchor="end"
              minTickGap={1}
              interval={0}
              height={barLineXAxisHeight}
              tick={{ fontSize: '10px', dy: barLineXAxisTickDy }}
            />
            <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: '10px' }} width={40} />
            <ShadcnChartTooltip cursor={true} content={<ChartTooltipContent indicator="dot" hideLabel />} />
            {composedSpec.series.map((s, index) => {
              const color = defaultChartColors[index % defaultChartColors.length];
              if (s.render === 'line') {
                return (
                  <Line key={s.key} type="monotone" dataKey={s.key} stroke={color} strokeWidth={2.5}
                    dot={{ r: 4, fill: color, strokeWidth: 0 }} />
                );
              }
              if (s.render === 'area') {
                return (
                  <Area key={s.key} type="monotone" dataKey={s.key} stroke={color} fill={color} fillOpacity={0.2} strokeWidth={2} />
                );
              }
              return <Bar key={s.key} dataKey={s.key} fill={color} radius={[5, 5, 0, 0]} />;
            })}
            <ShadcnChartLegend content={<ChartLegendContent className="flex-wrap justify-center" />} />
          </ComposedChart>
        );
      }
      default:
        return <p className="text-destructive">Unbekannter Diagrammtyp: {type}</p>;
    }
  };

  return (
    <div className="my-6 aspect-[16/10] md:aspect-[16/9]">
      <ChartContainer config={rechartsChartConfig} className="min-h-[300px] w-full">
        {renderChart()}
      </ChartContainer>
    </div>
  );
}
